import React from "react";
import { Box, Typography, styled } from "@mui/material";

interface StepperProps {
  currentStep: number;
  errorStep?: number;
  errorMessage?: string;
  isComplete?: boolean;
}

const StepperContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "stretch",
  gap: "0px",
  width: "100%",
  minWidth: "500px", // Reduce minimum width to prevent compression
}));

const StepContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "0px",
  flex: 1,
  minWidth: "100px", // Reduce minimum width for each step
  "&:not(:last-child)": {
    paddingRight: "0px",
  },
}));

const StepIcon = styled(Box)<{
  completed?: boolean;
  active?: boolean;
  error?: boolean;
}>(({ completed, active, error }) => ({
  width: "26px",
  height: "26px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: error ? "#FF4444" : completed ? "#0076CE" : "transparent",
  border: `2px solid ${
    error ? "#FF4444" : completed ? "#0076CE" : active ? "#0076CE" : "#C8C7C6"
  }`,
  position: "relative",
  flexShrink: 0, // Prevent icon from shrinking
  minWidth: "26px", // Ensure minimum width
  minHeight: "26px", // Ensure minimum height
}));

const ActiveDot = styled(Box)(() => ({
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  backgroundColor: "#0076CE",
}));

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path
      d="M16.6668 5L7.50016 14.1667L3.3335 10"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path
      d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z"
      fill="white"
    />
  </svg>
);

const StepLabel = styled(Typography)<{ error?: boolean }>(({ error }) => ({
  fontSize: "12px",
  fontWeight: 500,
  color: error ? "#FF4444" : "#071D49",
  textAlign: "left",
  fontFamily: "Libre Franklin, Arial, sans-serif",
  lineHeight: "1.2em",
  whiteSpace: "nowrap",
  padding: "0 2px", // Reduce horizontal padding to match the image
}));

const ConnectorLine = styled(Box)<{ completed?: boolean; error?: boolean }>(
  ({ completed, error }) => ({
    height: "2px",
    flexGrow: 1,
    minWidth: "40px",
    backgroundColor: error ? "#FF4444" : completed ? "#0076CE" : "#C8C7C6",
    marginLeft: "8px",
    marginRight: "8px",
  })
);

const ErrorMessage = styled(Typography)(() => ({
  fontSize: "12px",
  fontWeight: 400,
  color: "#FF4444",
  textAlign: "center",
  fontFamily: "Inter, sans-serif",
  lineHeight: "1.4em",
  marginTop: "4px",
}));

const EnrollmentStatusStepper: React.FC<StepperProps> = ({
  currentStep,
  errorStep,
  errorMessage,
  isComplete = false,
}) => {
  const steps = [
    { id: 1, label: "Generate PDF" },
    { id: 2, label: "Send to Customer" },
    { id: 3, label: "Customer Signs" },
    { id: 4, label: "Complete" },
  ];

  const renderStepIcon = (stepId: number) => {
    const isCompleted = isComplete ? stepId <= 4 : stepId < currentStep;
    const isActive = !isComplete && stepId === currentStep;
    const hasError = stepId === errorStep;

    if (hasError) {
      return (
        <StepIcon error>
          <ErrorIcon />
        </StepIcon>
      );
    }

    if (isCompleted) {
      return (
        <StepIcon completed>
          <CheckIcon />
        </StepIcon>
      );
    }

    if (isActive && stepId < 4) {
      return (
        <StepIcon active>
          <ActiveDot />
        </StepIcon>
      );
    }

    return <StepIcon />;
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <StepperContainer>
        {steps.map((step, index) => {
          const hasError = step.id === errorStep;
          const showError = hasError && errorMessage;

          return (
            <StepContainer
              key={step.id}
              sx={
                index === steps.length - 1 ? { flex: 0, paddingRight: 0 } : {}
              }
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "2px",
                  minWidth: 0,
                }}
              >
                {renderStepIcon(step.id)}
                <StepLabel
                  error={hasError}
                  sx={{
                    whiteSpace: "normal",
                    lineHeight: "1.2em",
                    textAlign: "center",
                    wordBreak: "break-word",
                  }}
                >
                  {step.label}
                </StepLabel>
              </Box>
              {index < steps.length - 1 && (
                <ConnectorLine
                  completed={isComplete ? true : step.id < currentStep}
                  error={hasError || (errorStep ? step.id < errorStep : false)}
                />
              )}
            </StepContainer>
          );
        })}
      </StepperContainer>
    </Box>
  );
};

export default EnrollmentStatusStepper;
